import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useUserSettings } from 'src/Hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { Banner, BannerLocations } from '../types'

const DEFAULT_BANNER_SKELETON_COUNT = 1

type Props = {
  location: BannerLocations
  bannersInCurrentLocation: Banner[]
  isBannersLoading: boolean
}

/**
 * Returns how many banner skeletons to reserve for a location while its banners are still loading.
 */
export const useBannerSkeletonCount = ({ location, bannersInCurrentLocation, isBannersLoading }: Props): number => {
  const { userSettings, updateUserSettings } = useUserSettings()
  const { installationId } = useParams<{ installationId: string }>()
  const isStorageLoading = useTypedSelector(state => state.storage.storageLoading)
  const installationIdFromState = useTypedSelector(state => state.storage.installation)?.id?.toString()

  const savedSkeletonCount =
    installationId !== undefined
      ? userSettings?.bannerSkeletonCounts?.[installationId]?.[location]
      : userSettings?.bannerSkeletonCounts?.general?.[location]

  /**
   * Once the banners for this location have fully resolved, persist the amount in user settings, for next renders
   */
  useEffect(() => {
    const isSettled = !isStorageLoading && installationId === installationIdFromState && !isBannersLoading
    if (!isSettled) return

    const resolvedSkeletonCount = new Set(bannersInCurrentLocation.map(banner => banner.type)).size
    if (resolvedSkeletonCount === savedSkeletonCount) return

    updateUserSettings({
      bannerSkeletonCounts: {
        ...userSettings?.bannerSkeletonCounts,
        ...(installationId === undefined
          ? {
              general: {
                ...userSettings?.bannerSkeletonCounts?.general,
                [location]: resolvedSkeletonCount,
              },
            }
          : {
              [installationId]: {
                ...userSettings?.bannerSkeletonCounts?.[installationId],
                [location]: resolvedSkeletonCount,
              },
            }),
      },
    }).catch(() => {
      console.error(`Failed to persist banner skeleton count for installation ${installationId} and location ${location}`)
    })
  }, [
    savedSkeletonCount,
    location,
    bannersInCurrentLocation,
    isBannersLoading,
    isStorageLoading,
    installationId,
    installationIdFromState,
    userSettings,
    updateUserSettings,
  ])

  return savedSkeletonCount ?? DEFAULT_BANNER_SKELETON_COUNT
}

export default useBannerSkeletonCount
